import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLanguage } from '../context/LanguageContext';

const TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const WARNING_MS = 2 * 60 * 1000;  // Show warning 2 min before

export default function SessionTimeout() {
  const { t } = useLanguage();
  const { isAuthenticated, logout } = useAuth0();
  const localUser = JSON.parse(localStorage.getItem('ct_user') || 'null');
  const loggedIn = isAuthenticated || !!localUser;

  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(120);
  const timerRef = useRef(null);
  const warningRef = useRef(null);
  const countdownRef = useRef(null);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('ct_token');
    localStorage.removeItem('ct_user');
    window.dispatchEvent(new Event('ct_auth_change'));
    if (isAuthenticated) logout({ logoutParams: { returnTo: window.location.origin } });
    else window.location.href = '/';
  }, [isAuthenticated, logout]);

  const resetTimers = useCallback(() => {
    setShowWarning(false);
    setCountdown(120);
    clearTimeout(timerRef.current);
    clearTimeout(warningRef.current);
    clearInterval(countdownRef.current);

    if (!loggedIn) return;

    warningRef.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(120);
      countdownRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) { clearInterval(countdownRef.current); return 0; }
          return prev - 1;
        });
      }, 1000);
    }, TIMEOUT_MS - WARNING_MS);

    timerRef.current = setTimeout(handleLogout, TIMEOUT_MS);
  }, [loggedIn, handleLogout]);

  useEffect(() => {
    if (!loggedIn) return;
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetTimers, { passive: true }));
    resetTimers();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimers));
      clearTimeout(timerRef.current);
      clearTimeout(warningRef.current);
      clearInterval(countdownRef.current);
    };
  }, [loggedIn, resetTimers]);

  if (!loggedIn || !showWarning) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center" data-testid="session-timeout-modal">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-sm mx-4 shadow-2xl">
        <h3 className="font-serif text-lg font-bold text-navy dark:text-white mb-2">Session Expiring</h3>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          Your session will expire in <span className="font-bold text-terracotta">{countdown}s</span> due to inactivity.
        </p>
        <div className="flex gap-3">
          <button
            onClick={resetTimers}
            data-testid="session-stay-btn"
            className="flex-1 px-4 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors"
          >
            Stay Logged In
          </button>
          <button
            onClick={handleLogout}
            data-testid="session-logout-btn"
            className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            Log Out
          </button>
        </div>
      </div>
    </div>
  );
}
